import { Grid, Typography } from '@mui/material';
import {
    CampaignRequestAddActualAppointmentButton,
    CampaignRequestHeader as CampaignRequestHeaderICS,
    CampaignRequestMenuButton,
    CampaignRequestUpdateAppointmentButton,
    clientRoute,
    formatDateAndTime,
    ICSDataTestIds,
    useCampaignRequestPageContext,
} from '@platform/ics-front-core';
import {
    ClientBreadcrumbsWithCustomLinks,
    LinkToPerson,
    ObjectHeaderLayout,
    useFetchAllPermissions,
} from '@platform/front-core';
import { CommonInfoLine, ConfirmationDialog, Link, Transitions } from '@platform/front-ui';
import { observer } from 'mobx-react-lite';
import React, { ReactNode, useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { generatePath, Route, useHistory, useParams } from 'react-router-dom';
import { injectable } from 'react-magnetic-di';
import { RenderTransitionDialogProps } from '@platform/front-ui';

const MODER_RKK_STATE_CODE = 'moderRKK';
const MODER_RKK_CONFIRM_MESSAGE =
    'Перед отправкой на модерацию опишите внесённые изменения в поле «Комментарии» — так модератор быстрее проверит заявку';

export const CampaignRequestHeader = observer((): JSX.Element => {
    const { isSyslog, model: requestModel, campaignRequestTransitionsModel } = useCampaignRequestPageContext();
    const {
        campaign,
        currentSection,
        loadExecutor,
        loadRequest,
        number,
        regEnd,
        sent,
        setCurrentUserAsExecutor,
        state,
        validateReadonlyPage,
    } = requestModel;

    const { formatMessage } = useIntl();
    const params = useParams<{ id: string; sectionId?: string }>();
    const history = useHistory();

    const { id: requestId } = params;

    const allPermissionsQueries = useMemo(
        () => [
            { entityCode: 'CampaignRequest', permCode: 'AddActualAppointment', entityId: requestId },
            { entityCode: 'CampaignRequest', permCode: 'UpdateActualAppointment', entityId: requestId },
        ],
        [requestId],
    );
    const [permissionsData, reloadPermissions] = useFetchAllPermissions({ allPermissionsQueries });
    const [canAddActualAppointment, canUpdateActualAppointment] = permissionsData;

    const handleAppointmentSuccess = (): void => {
        reloadPermissions();
        loadExecutor();
    };

    const handleSetCurrentUserAsExecutor = (): Promise<void> =>
        setCurrentUserAsExecutor().then(handleAppointmentSuccess);

    const executorElement = currentSection?.executor ? (
        <Grid container alignItems="center">
            <Grid item pr={0.5}>
                <LinkToPerson person={currentSection.executor} isWithInitials />
            </Grid>
            {canUpdateActualAppointment && (
                <Grid item>
                    <CampaignRequestUpdateAppointmentButton
                        handleSuccessSubmit={handleAppointmentSuccess}
                        isIconButton
                    />
                </Grid>
            )}
        </Grid>
    ) : null;

    const breadcrumbs = useMemo(
        () => [
            {
                routeTitle: formatMessage({ id: 'breadcrumbs.campaigns' }),
                to: clientRoute.campaigns,
            },
            {
                routeTitle: campaign?.title ?? '',
                to: campaign?.id ? generatePath(clientRoute.campaign, { id: campaign.id }) : '',
            },
            {
                routeTitle: formatMessage({ id: 'campaignRequest.requestN' }, { number }),
                to: requestId ? generatePath(clientRoute.request, { id: requestId }) : '',
            },
            ...(isSyslog
                ? [
                      {
                          routeTitle: formatMessage({ id: 'breadcrumbs.syslog' }),
                          to: clientRoute.requestSyslog,
                      },
                  ]
                : []),
        ],
        [campaign, formatMessage, isSyslog, number, requestId],
    );

    const sectionId = params.sectionId;
    const editSectionFormPath = sectionId ? generatePath(clientRoute.requestSectionFormEdit, params) : '';

    const readonlyValidationMessage = useMemo(
        () => (
            <FormattedMessage
                id="campaignRequest.readonlyRequestValidationMsg"
                values={{
                    a: (children: ReactNode) => (
                        <Link href={editSectionFormPath} sx={{ display: 'inline-block' }} isWithoutAutoCapitalize>
                            {children}
                        </Link>
                    ),
                }}
            />
        ),
        [editSectionFormPath],
    );

    const renderTransitionDialog = useCallback(
        (dialogProps: RenderTransitionDialogProps): JSX.Element => {
            const { transition, isOpen, onClose, onConfirm } = dialogProps;
            const isModerRKK = transition.toStateCode === MODER_RKK_STATE_CODE;

            return (
                <ConfirmationDialog
                    id="confirm-transition"
                    keepMounted
                    open={isOpen}
                    onCancel={onClose}
                    onConfirm={onConfirm}
                    title={formatMessage({ id: 'common.lifeCycleConfirmTitle' })}
                    message={
                        isModerRKK
                            ? MODER_RKK_CONFIRM_MESSAGE
                            : formatMessage({ id: 'common.lifeCycleConfirmText' }, { toState: transition.toStateTitle })
                    }
                />
            );
        },
        [formatMessage],
    );

    const objectHeaderLayoutProps = {
        breadcrumbs: <ClientBreadcrumbsWithCustomLinks customLinks={breadcrumbs} />,
        mainContentContainerProps: { sx: { '&.MuiGrid-item': { pt: 0.5 } } },
        titleBlockAndMenu: {
            containerProps: { sx: { pb: 1 } },
            menu: { element: <CampaignRequestMenuButton /> },
            titleBlock: (
                <Typography pt={0.5} variant="subtitle2">
                    <FormattedMessage id="campaignRequest.requestN" values={{ number }} />
                </Typography>
            ),
        },
        rightBlocks: {
            topBlock: (
                <>
                    <Grid
                        container
                        data-testid={ICSDataTestIds.campaignRequestCardHeaderRequestInfo}
                        flexDirection="column"
                        pb={1.25}
                        spacing={1}
                    >
                        {!sent && (
                            <CommonInfoLine
                                title={formatMessage({ id: 'campaignRequest.fields.submissionDeadline' })}
                                value={formatDateAndTime(regEnd)}
                            />
                        )}
                        <CommonInfoLine
                            title={formatMessage({ id: 'campaignRequest.fields.state' })}
                            value={state.title}
                        />
                        {executorElement && (
                            <CommonInfoLine
                                isColumnDirection
                                title={formatMessage({ id: 'campaignRequest.fields.executor' })}
                                value={executorElement}
                            />
                        )}
                    </Grid>
                    {canAddActualAppointment && (
                        <Grid container flexDirection="column">
                            <Grid item pb={0.5}>
                                <CampaignRequestAddActualAppointmentButton
                                    handleConfirm={handleSetCurrentUserAsExecutor}
                                    requestNumber={number}
                                />
                            </Grid>
                            {canUpdateActualAppointment && (
                                <Grid item>
                                    <CampaignRequestUpdateAppointmentButton
                                        handleSuccessSubmit={handleAppointmentSuccess}
                                    />
                                </Grid>
                            )}
                        </Grid>
                    )}
                </>
            ),
        },
        leftBottomBlock: {
            element: (
                <Route
                    exact
                    path={[
                        clientRoute.requestSection,
                        clientRoute.requestSectionRelatedObjects,
                        clientRoute.requestSectionAddRelatedObjects,
                    ]}
                >
                    <Transitions
                        model={campaignRequestTransitionsModel}
                        onSuccessTransition={() => {
                            loadRequest();
                            reloadPermissions();
                            currentSection?.printForms?.load();
                        }}
                        onValidationErrorDialogButtonClick={() => history.push(editSectionFormPath)}
                        renderTransitionDialog={renderTransitionDialog}
                        validateFunction={validateReadonlyPage}
                        validationButtonText={formatMessage({
                            id: 'campaignRequest.readonlyRequestValidationBtnMsg',
                        })}
                        validationErrorMessage={readonlyValidationMessage}
                    />
                </Route>
            ),
        },
    };

    return <ObjectHeaderLayout {...objectHeaderLayoutProps} />;
});

export const CampaignRequestHeaderDI = injectable(CampaignRequestHeaderICS, CampaignRequestHeader);
